import React, { ChangeEvent, ReactElement, useEffect, useRef, useState } from 'react';

import {
  containsImageOrVideo,
  extractWordAtCaretPosition,
  generateUUID,
  getClassNames,
  isHashtag,
  isMention,
  replaceWordAtCaretPosition
} from '../../../../../utils/util';

import { FileType } from '../../../../../types/common.types';
import { HashTag, Member, Poll, PollOption, Post } from '../../../../../types/entity.types';

import TribeFilesContainer from '../../../../../containers/programme/tribe/tribe-files/TribeFiles.container';

import Button from '../../../../common/button/Button';
import FilePicker from '../../../../common/input/file-picker/FilePicker';
import TribePollModal from '../tribe-poll-modal/TribePollModal';
import TribePoll from '../tribe-poll/TribePoll';
import TribeThumbnails from '../tribe-thumbnails/TribeThumbnails';

import { ReactComponent as PaperclipIcon } from '../../../../../resources/icons/paperclip.svg';
import { ReactComponent as PollIcon } from '../../../../../resources/icons/poll.svg';

import './tribe-input.scss';

type Props = {
  post?: Post;
  placeholder: string;
  files?: FileType[];
  hashtags?: HashTag[];
  members?: Member[];
  comment?: boolean;
  addAttachment: (file: File | undefined) => void;
  removeAttachment: (file: File | FileType) => void;
  createPost: (value: string) => Promise<void>;
  updatePost: (value: string) => Promise<void>;
  createComment: (message: string, callbackFn: () => void) => Promise<void>;
  createPoll: (message: string, poll: Poll) => Promise<void>;
  updatePoll: (message: string, poll: Poll) => Promise<void>;
  focus?: boolean;
};

const TribeInput = ({
  post,
  placeholder,
  files = [],
  hashtags = [],
  members = [],
  comment = false,
  addAttachment,
  removeAttachment,
  createPost,
  updatePost,
  createComment,
  createPoll,
  updatePoll,
  focus = false
}: Props): ReactElement => {
  const [message, setMessage] = useState<string>('');
  const [caretPosition, setCaretPosition] = useState<number | undefined>(undefined);
  const [suggestions, setSuggestions] = useState<HashTag[] | Member[]>([]);
  const [showPollModal, setShowPollModal] = useState<boolean>(false);
  const [poll, setPoll] = useState<Poll | undefined>(undefined);

  const textareaRef: React.MutableRefObject<HTMLTextAreaElement | null> = useRef<HTMLTextAreaElement | null>(null);
  const fileRef: React.MutableRefObject<HTMLInputElement | null> = useRef<HTMLInputElement | null>(null);

  useEffect((): void => {
    if (focus) {
      textareaRef.current?.focus();
    }
  }, [focus]);

  useEffect((): void => {
    if (post) {
      setMessage(post.text ?? '');

      const { question, options }: Post = post;

      if (question && options) {
        const poll: Poll = {
          question,
          options: options.map((option: string): PollOption => {
            return { id: generateUUID(), value: option, votes: 0 };
          }),
          allowMultipleSelection: post.allowMultipleSelection
        };

        setPoll(poll);
      }
    }
  }, [post]);

  const filterSuggestions = (word: string): void => {
    if (isHashtag(word)) {
      setSuggestions(
        hashtags.filter((hashtagInList: HashTag): boolean => {
          if (!hashtagInList || !hashtagInList.name) {
            return false;
          }

          return hashtagInList.name.toLowerCase().includes(word.toLowerCase());
        })
      );
    } else if (isMention(word)) {
      setSuggestions(
        members.filter(
          (member: Member): boolean =>
            member.username.toLowerCase().includes(word.substring(1).toLowerCase()) &&
            !member.username.includes('Deleted Account')
        )
      );
    } else {
      setSuggestions([]);
    }
  };

  const handleTextareaClick = ({ target }: React.MouseEvent<HTMLTextAreaElement>): void => {
    const { textContent }: HTMLTextAreaElement = target as HTMLTextAreaElement;
    const caretPosition: number | undefined = textareaRef.current?.selectionEnd;
    const [word] = extractWordAtCaretPosition(textContent, caretPosition);

    filterSuggestions(word);
    setCaretPosition(caretPosition);
  };

  const handleTextareaKeyUp = ({ keyCode, target }: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (keyCode < 41 && keyCode > 36) {
      const { value }: HTMLTextAreaElement = target as HTMLTextAreaElement;
      const caretPosition: number | undefined = textareaRef.current?.selectionEnd;
      const [word] = extractWordAtCaretPosition(value, caretPosition);

      filterSuggestions(word);
      setCaretPosition(caretPosition);
    }
  };

  const handleMessageChange = ({ target }: ChangeEvent<HTMLTextAreaElement>): void => {
    const { value }: EventTarget & HTMLTextAreaElement = target;
    const caretPosition: number | undefined = textareaRef.current?.selectionEnd;
    const [word] = extractWordAtCaretPosition(value, caretPosition);

    filterSuggestions(word);
    setCaretPosition(caretPosition);
    setMessage(value);
  };

  const handleSuggestionClick = (suggestion: string): void => {
    const isHashtagSuggestion: boolean = suggestion.includes('#');

    let newMessage: string;
    if (isHashtagSuggestion) {
      newMessage = replaceWordAtCaretPosition(message, suggestion, caretPosition);
    } else {
      newMessage = replaceWordAtCaretPosition(message, `@${suggestion}`, caretPosition);
    }

    setMessage(newMessage);
    textareaRef.current?.focus();
    setSuggestions([]);
  };

  const handlePostClick = async (): Promise<void> => {
    if (comment) {
      await createComment(message, (): void => setMessage(''));
    } else if (post && poll) {
      await updatePoll(message, poll);
    } else if (post) {
      await updatePost(message);
    } else if (poll) {
      await createPoll(message, poll);
    } else {
      await createPost(message);
    }
  };

  const hasFiles: boolean = files.length > 0;

  return (
    <div className={getClassNames('tribe-input', { comment })} data-testid="tribe-input">
      <textarea
        value={message}
        onClick={handleTextareaClick}
        onChange={handleMessageChange}
        onKeyUp={handleTextareaKeyUp}
        placeholder={placeholder}
        ref={textareaRef}
      />
      {suggestions.length > 0 && (
        <div
          className={getClassNames('tribe-input__suggestions', {
            comment,
            border: hasFiles,
            margin: hasFiles && !containsImageOrVideo(files)
          })}
        >
          {suggestions.map((suggestion: HashTag | Member): ReactElement => {
            const value: string = ((suggestion as HashTag).name || (suggestion as Member).username) as string;
            return (
              <p
                className={getClassNames('tribe-input__suggestion', { comment })}
                onClick={(): void => handleSuggestionClick(value)}
                key={value}
              >
                {value}
              </p>
            );
          })}
        </div>
      )}
      <TribeThumbnails files={files} deleteFile={removeAttachment} comment={comment} />
      <TribeFilesContainer files={files} deleteFile={removeAttachment} comment={comment} />
      {poll && (
        <TribePoll
          poll={poll}
          onEditClick={(): void => setShowPollModal(true)}
          onDeleteClick={(): void => setPoll(undefined)}
        />
      )}
      <div className={getClassNames('tribe-input__actions', { comment })}>
        <div className="tribe-input__actions__icon-container">
          <FilePicker
            triggerElement={
              <PaperclipIcon
                className="tribe-input__paperclip-icon"
                onClick={(): void => fileRef.current?.click()}
                data-testid="tribe-input-attachment-icon"
              />
            }
            fileRef={fileRef}
            onChange={({ target }: ChangeEvent<HTMLInputElement>): void =>
              addAttachment(target.files ? target.files[0] : undefined)
            }
          />
          {!(comment || (post && !poll)) && (
            <PollIcon
              className="tribe-input__poll-icon"
              data-testid="tribe-input-poll-icon"
              onClick={(): void => setShowPollModal(true)}
            />
          )}
        </div>
        <Button text={post ? 'SAVE' : 'POST'} onClick={handlePostClick} />
      </div>

      <TribePollModal show={showPollModal} setShow={setShowPollModal} poll={poll} setPoll={setPoll} />
    </div>
  );
};

export default TribeInput;
